import { useCallback, useEffect, useState } from "react";
import { HandleSSOCallback, useClerk, useSignIn, useSignUp } from "@clerk/react";
import { AuthLayout, describeSignInError, SignInScreen } from "@otrack/client";

/** Where Google sends the browser back when a sign-in needs Clerk to finish it here. */
export const SSO_CALLBACK_PATH = "/sso-callback";

/** The sign-in screen, starting Google directly through Clerk rather than its modal. */
export function WebSignInScreen({ error = null }: { error?: string | null }) {
  const { signIn } = useSignIn();
  const start = useCallback(async () => {
    const result = await signIn.sso({ strategy: "oauth_google", redirectUrl: "/", redirectCallbackUrl: SSO_CALLBACK_PATH }).catch((cause: unknown) => ({ error: cause as { message?: string } }));
    // On success the browser is already on its way to Google.
    return result.error ? describeSignInError(result.error) ?? "Google sign-in couldn't start. Check your connection and try again." : null;
  }, [signIn]);
  return <SignInScreen start={start} error={error}/>;
}

/**
 * Finishes a Google sign-in that came back through SSO_CALLBACK_PATH. Anything that needs more
 * than Google (a second factor, a new account's details) goes to Clerk's own sign-in page.
 */
export function SsoCallbackScreen() {
  const clerk = useClerk();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const [failed, setFailed] = useState<string | null>(null);

  const problem = describeSignInError(signIn.firstFactorVerification.error ?? signUp.verifications.externalAccount.error);
  useEffect(() => {
    if (!problem) return;
    window.history.replaceState(null, "", "/");
    setFailed(problem);
  }, [problem]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      window.history.replaceState(null, "", "/");
      setFailed("Google sign-in didn't finish. Try again.");
    }, 20_000);
    return () => window.clearTimeout(id);
  }, []);

  if (failed) return <WebSignInScreen error={failed}/>;
  const elsewhere = () => void clerk.redirectToSignIn({ signInForceRedirectUrl: "/", signUpForceRedirectUrl: "/" });
  return <AuthLayout>
    <h1 id="ot-auth-title">Signing you in</h1>
    <p className="ot-auth__lede" role="status">Finishing up with Google. This only takes a moment.</p>
    <div className="ot-auth__progress" aria-hidden="true"><i/></div>
    <HandleSSOCallback
      navigateToApp={({ decorateUrl }) => window.location.replace(decorateUrl("/"))}
      navigateToSignIn={elsewhere}
      navigateToSignUp={elsewhere}
    />
  </AuthLayout>;
}
